import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Link } from 'react-router-dom';
import { Cookie, Settings, X } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'cookie_consent';

export const getCookieConsent = () => {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  return consent ? JSON.parse(consent) : null;
};

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      setShowBanner(true);
    } else {
      setPreferences(consent);
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: new Date().toISOString()
    };
    saveConsent(allAccepted);
  };

  const rejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString()
    };
    saveConsent(onlyNecessary);
  };

  const savePreferences = () => {
    saveConsent({
      ...preferences,
      timestamp: new Date().toISOString()
    });
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-4"
        data-testid="cookie-banner"
      >
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          {!showSettings ? (
            // Main Banner
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {t('cookie.title')}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    {t('cookie.description')}{' '}
                    <Link to="/cookie-policy" className="text-indigo-600 hover:underline">
                      {t('cookie.cookiePolicy')}
                    </Link>{' '}
                    {t('cookie.and')}{' '}
                    <Link to="/privacy-policy" className="text-indigo-600 hover:underline">
                      {t('cookie.privacyPolicy')}
                    </Link>.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={acceptAll} className="btn-accent" data-testid="accept-all-cookies">
                      {t('cookie.acceptAll')}
                    </Button>
                    <Button onClick={rejectAll} variant="outline" data-testid="reject-all-cookies">
                      {t('cookie.rejectAll')}
                    </Button>
                    <Button 
                      onClick={() => setShowSettings(true)} 
                      variant="ghost"
                      className="flex items-center gap-2"
                      data-testid="cookie-settings"
                    >
                      <Settings className="w-4 h-4" />
                      {t('cookie.customize')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Settings Panel
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-900">
                  {t('cookie.settings')}
                </h3>
                <button 
                  onClick={() => setShowSettings(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium text-slate-900">{t('cookie.necessary')}</Label>
                    <p className="text-sm text-slate-500 mt-1">
                      {t('cookie.necessaryDesc')}
                    </p>
                  </div>
                  <Switch checked={true} disabled />
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium text-slate-900">{t('cookie.analytics')}</Label>
                    <p className="text-sm text-slate-500 mt-1">
                      {t('cookie.analyticsDesc')}
                    </p>
                  </div>
                  <Switch 
                    checked={preferences.analytics}
                    onCheckedChange={(checked) => setPreferences({...preferences, analytics: checked})}
                    data-testid="analytics-cookie-switch"
                  />
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium text-slate-900">{t('cookie.marketing')}</Label>
                    <p className="text-sm text-slate-500 mt-1">
                      {t('cookie.marketingDesc')}
                    </p>
                  </div>
                  <Switch 
                    checked={preferences.marketing}
                    onCheckedChange={(checked) => setPreferences({...preferences, marketing: checked})}
                    data-testid="marketing-cookie-switch"
                  />
                </div>

                {/* Functional Cookies */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium text-slate-900">{t('cookie.functional')}</Label>
                    <p className="text-sm text-slate-500 mt-1">
                      {t('cookie.functionalDesc')}
                    </p>
                  </div>
                  <Switch 
                    checked={preferences.functional}
                    onCheckedChange={(checked) => setPreferences({...preferences, functional: checked})}
                    data-testid="functional-cookie-switch"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={savePreferences} className="btn-accent" data-testid="save-cookie-preferences">
                  {t('cookie.savePreferences')}
                </Button>
                <Button onClick={acceptAll} variant="outline">
                  {t('cookie.acceptAll')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieBanner;
